import { createContext, useContext } from 'react'

import type { Locale } from '@app/api'

import { localeMessages, type TranslationKey } from './translations'

type TranslationParams = Record<string, string | number>

export interface I18nContextValue {
  locale: Locale
  t: (key: TranslationKey, params?: TranslationParams) => string
}

export const I18nContext = createContext<I18nContextValue | null>(null)

function interpolate(template: string, params: TranslationParams = {}): string {
  return Object.entries(params).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, String(value)),
    template
  )
}

function translate(
  locale: Locale,
  key: TranslationKey,
  params?: TranslationParams
): string {
  const messages = localeMessages[locale]
  const fallbackMessages = localeMessages.de
  const template = messages[key] ?? fallbackMessages[key] ?? key

  return interpolate(template, params)
}

export function createI18nContextValue(locale: Locale): I18nContextValue {
  return {
    locale,
    t(key, params) {
      return translate(locale, key, params)
    }
  }
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext)

  if (value === null) {
    throw new Error('useI18n must be used inside I18nProvider')
  }

  return value
}
